import { FilterControllerContext, FilterControllerT } from '../filter-controller';
import { FilterData, FilterTag } from '../filter-data';

/**
 * Defines a single item in the set filter.
 */
export interface SetItem {
  /**
   * Internal id of the item.
   */
  id: string;

  /**
   * Label to display on the filter UI for the item.
   */
  label: string;

  /**
   * Host provided data provided by the host for this item.  This passed through if the item is selected.
   */
  payload?: unknown;

  disabled?: boolean;
}

/**
 * Defines application-provided parameters for a set filter.
 */
export interface SetFilterParams {
  /**
   * Called to get a list of possible items the user can select from.
   */
  getItems: () => SetItem[];

  /**
   * Flag indicating whether or not this is a single-select or multi-select filter.
   */
  singleSelect?: boolean;

  /**
   * Placeholder text for internal search (default: 'Filter By').
   */
  searchHint?: string;

  /**
   * Show mini-search field to filter the value list.
   */
  showSearchInput?: boolean;

  /**
   * define a custom filter to find specific choices available in the Set
   * It receives the set of choices available and the search criteria.
   * It must go through the choices and set the isVisible flag for the filtered choices.
   * It must return the number of total visible choices
   */
  choicesFilter?: (choices: SetChoice[], value: string) => number;
}

/**
 * Defines user-configured data for a set filter.
 */
export class SetFilterData implements FilterData {
  /**
   * User-selected items.
   */
  items?: SetItem[];

  /** FilterData implementation */

  clear(): void {
    this.items = undefined;
  }

  copyFrom(data: SetFilterData): void {
    if (data?.items) {
      this.items = data.items.map(element => {
        // Create a copy of the object, but reference copy of the payload.
        return {
          id: element.id,
          label: element.label,
          payload: element.payload,
          disabled: element.disabled
        };
      });
    } else {
      this.items = undefined;
    }
  }

  getTags(): FilterTag[] {
    if (this.hasFilter()) {
      return this.items.map(element => {
        return {
          description: element.label,
          remove: () => {
            const index: number = this.items.findIndex(_element => _element.id === element.id);
            if (index !== -1) {
              const newFilterData = new SetFilterData();
              newFilterData.copyFrom(this);
              newFilterData.items.splice(index, 1);
              if (newFilterData.items.length === 0) {
                newFilterData.items = undefined;
              }
              return newFilterData;
            } else {
              return this;
            }
          }
        };
      });
    } else {
      return [];
    }
  }

  hasFilter(): boolean {
    return !!this.items && this.items.length > 0;
  }
}

interface SetChoice {
  item: SetItem;
  isSelected: boolean;
  isVisible: boolean;
}

/**
 * Defines the filter handler for a set filter.
 * The HTML template for the set filter is defined in virtual-grid-header-dropdown.html.
 */
export class SetFilterController extends FilterControllerT<SetFilterData, SetFilterParams> {
  constructor(context: FilterControllerContext) {
    super(context, () => {
      return new SetFilterData();
    });

    this.initialFocusElementName = 'search-input';

    this.state = {};
    this.state.choices = [];
    this.state.lastChoice = undefined;
    this.state.choiceFilter = undefined;
    this.state.isAllChecked = false;
    this.state.isAllIndeterminate = false;
    this.state.totalVisible = 0;
  }

  getTemplateContext(): any {
    const context = super.getTemplateContext();

    context.state = this.state;
    context.onSelectAll = () => {
      this.state.isAllChecked = !this.state.isAllChecked;
      this.state.isAllIndeterminate = false;
      this.state.choices.forEach(choice => {
        if (choice.isVisible && !choice.item.disabled) {
          choice.isSelected = this.state.isAllChecked;
        }
      });
    };
    context.onSelectChoice = (choice: SetChoice) => {
      choice.isSelected = !choice.isSelected;
      if (this.state.lastChoice) {
        if (choice.item.id !== this.state.lastChoice.item.id) {
          if (this.sourceParams?.singleSelect) {
            this.state.lastChoice.isSelected = false;
          }
        }
      }
      this.state.lastChoice = choice;
      this.evaluateSelectAllState();
    };
    context.onChoiceFilterChange = (value: string) => {
      if (this.state.choiceFilter !== value) {
        // if cohicesFilter is provided in the params, use the provided one.
        const choicesFilter = this.sourceParams?.choicesFilter || this.choicesFilter;
        this.state.totalVisible = choicesFilter(this.state.choices, value);
        this.state.choiceFilter = value;
        this.evaluateSelectAllState();
      }
    };
    context.removeSearch = (): void => {
      this.state.choices.forEach(choice => {
        choice.isVisible = true;
      });
      this.state.choiceFilter = undefined;
      this.state.totalVisible = this.state.choices.length;
      this.evaluateSelectAllState();
      this.focusInitialElement();
    };
    return context;
  }

  canClear(): boolean {
    // For the set filter, we don't updated the values array until the UI is evaluated.  So in this
    // case, there is a filter if any choice is selected.
    return this.state.choices.some(choice => choice.isSelected);
  }

  onClear(): void {
    // For the set filter, we don't updated the values array until the UI is evaluated.  So we need to
    // update the UI.
    this.state.choices.forEach(choice => (choice.isSelected = false));
    // Update the select all state.
    this.state.isAllChecked = false;
    this.state.isAllIndeterminate = false;
  }

  protected onInit(): void {
    super.onInit();

    const options = (this.sourceParams?.getItems() || []).map(element => {
      return {
        item: element,
        isSelected: this.localData.items?.find(_element => _element.id === element.id) !== undefined || false,
        isVisible: true
      };
    });

    this.state.choices = options;
    this.state.totalVisible = this.state.choices.length;
    this.state.lastChoice = undefined;
    this.state.choiceFilter = undefined;
    this.evaluateSelectAllState();
  }

  protected evaluateLocalData(): boolean {
    const target = this.localData;
    const source = this.sourceData;

    target.items = this.state.choices.filter(choice => choice.isSelected).map(choice => choice.item);

    const targetHasFilter = target.hasFilter();
    const sourceHasFilter = !!source?.hasFilter();

    if (targetHasFilter && sourceHasFilter) {
      return !(
        target.items.length === source.items.length &&
        target.items.every((element, index) => element.id === source.items[index].id)
      );
    } else {
      return targetHasFilter !== sourceHasFilter;
    }
  }

  private evaluateSelectAllState(): void {
    const visibleChoices = this.state.choices.filter(choice => choice.isVisible && !choice.item.disabled);
    if (visibleChoices.length > 0) {
      const selectedChoices = visibleChoices.filter(choice => choice.isSelected);
      if (selectedChoices.length === 0) {
        this.state.isAllChecked = false;
        this.state.isAllIndeterminate = false;
      } else if (selectedChoices.length === visibleChoices.length) {
        this.state.isAllChecked = true;
        this.state.isAllIndeterminate = false;
      } else {
        this.state.isAllChecked = false;
        this.state.isAllIndeterminate = true;
      }
    } else {
      this.state.isAllChecked = false;
      this.state.isAllIndeterminate = false;
    }
  }

  private choicesFilter(choices: SetChoice[], value: string): number {
    const regExp = new RegExp(value, 'i');
    let totalVisible = 0;
    choices.forEach(choice => {
      choice.isVisible = regExp.test(choice.item.label);
      if (choice.isVisible) {
        totalVisible++;
      }
    });
    return totalVisible;
  }

  private state: {
    choices?: SetChoice[];
    lastChoice?: SetChoice;
    choiceFilter?: string;
    isAllChecked?: boolean;
    isAllIndeterminate?: boolean;
    totalVisible?: number;
  };
}
